import { cn } from '@/lib/utils';

interface LoadingStateProps {
  message?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Consistent loading spinner used across all pages.
 * Replaces the variety of inline spinners with different colors.
 */
export function LoadingState({
  message = 'Loading...',
  className,
  size = 'md',
}: LoadingStateProps) {
  const sizeClasses = {
    sm: 'w-3 h-3 border-[1.5px]',
    md: 'w-4 h-4 border-2',
    lg: 'w-6 h-6 border-2',
  };

  return (
    <div
      className={cn('flex items-center justify-center py-8', className)}
      role="status"
      aria-label={message}
    >
      <div className="flex items-center gap-2">
        <div
          className={cn(
            sizeClasses[size],
            'border-border border-t-primary rounded-full animate-spin'
          )}
        />
        <span className="text-muted-foreground text-sm">{message}</span>
      </div>
    </div>
  );
}

/**
 * Inline spinner for use inside buttons or search inputs.
 */
export function InlineSpinner({
  className,
  size = 'sm',
}: {
  className?: string;
  size?: 'sm' | 'md';
}) {
  const sizeClasses = {
    sm: 'w-4 h-4 border-2',
    md: 'w-5 h-5 border-2',
  };

  return (
    <div
      className={cn(
        sizeClasses[size],
        'border-border border-t-primary rounded-full animate-spin',
        className
      )}
      role="status"
      aria-label="Loading"
    />
  );
}
